import React, { useRef, useState } from "react";

// Posts a photo of TODAY's food special. Used in two places:
//   - /staff-post        (bartender/cook on their phone, staff code, no login)
//   - /owner/todays-special (owner in the dashboard, logged in)
// It shows in the customer app until midnight, then drops off by itself.

export const POST_TODAYS_SPECIAL_URL =
  "https://us-central1-bar-buzz-a6645.cloudfunctions.net/postTodaysSpecial";

// Shrinks a phone photo (often 5–12 MB) down to a sharp ~1600px JPEG so it
// uploads fast on bar Wi-Fi. Returns plain base64 (no "data:" prefix).
function resizeToJpegBase64(file, maxSize = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      resolve({ base64: dataUrl.split(",")[1], preview: dataUrl });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Couldn't read that photo. Try taking it again."));
    };
    img.src = url;
  });
}

const box = {
  background: "#1A1528",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "14px",
  padding: "20px",
};

const bigButton = {
  width: "100%",
  minHeight: "56px",
  border: "none",
  borderRadius: "12px",
  fontSize: "19px",
  fontWeight: 700,
  cursor: "pointer",
};

/**
 * Props:
 *  - getAuthBody: async () => object merged into the request body
 *      (staff: { staffCode }, owner: { barId })
 *  - getAuthHeaders: async () => object of extra headers (owner: Authorization)
 *  - disabled: true when there's nothing to post for yet (no bar / no code)
 */
// Text for each kind of post.
const KINDS = {
  food: {
    heading: "Today's food special",
    blurb: "Snap a photo of today's plate. Customers nearby see it right away, and it disappears at midnight. Posting again today replaces the photo.",
    label: "What is it?",
    placeholder: 'e.g. "Walleye basket with fries — $12"',
    needCaption: 'Add a short description, like "Walleye basket $12".',
    done: (bar) => `Posted! It's live for ${bar} until midnight.`,
  },
  pulltabs: {
    heading: "Pull-tab update",
    blurb: "Post a photo of your pull-tab boxes or winner board. It shows on your bar's page and in the app's Pulltabs tab for 24 hours. Posting again replaces it.",
    label: "Say something about it",
    placeholder: 'e.g. "New $599 game just opened"',
    needCaption: 'Add a short line, like "New game just opened".',
    done: (bar) => `Posted! Your pull-tab update is live for ${bar} for the next 24 hours.`,
  },
};

const TERMS_KEY = "bff_post_terms_ok";
function readTermsOk() {
  try {
    return window.localStorage.getItem(TERMS_KEY) === "yes";
  } catch {
    return false;
  }
}

export default function TodaysSpecialPoster({ getAuthBody, getAuthHeaders, disabled, kind = "food" }) {
  const text = KINDS[kind] || KINDS.food;
  const fileInput = useRef(null);
  const [photo, setPhoto] = useState(null); // { base64, preview }
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [termsOk, setTermsOk] = useState(readTermsOk());

  const toggleTerms = (on) => {
    setTermsOk(on);
    try {
      if (on) window.localStorage.setItem(TERMS_KEY, "yes");
      else window.localStorage.removeItem(TERMS_KEY);
    } catch {
      // private browsing: they'll just check it again next time
    }
  };

  const pickPhoto = async (file) => {
    if (!file) return;
    setError("");
    setSuccess("");
    try {
      setPhoto(await resizeToJpegBase64(file));
    } catch (e) {
      setError(e.message);
    }
  };

  const post = async () => {
    setError("");
    setSuccess("");
    if (!photo) return setError("Take or choose a photo first.");
    if (!caption.trim()) return setError(text.needCaption);
    if (!termsOk) return setError("Please check the box saying you're responsible for what you post.");

    setBusy(true);
    try {
      const res = await fetch(POST_TODAYS_SPECIAL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await getAuthHeaders()) },
        body: JSON.stringify({
          ...(await getAuthBody()),
          kind,
          acceptTerms: true,
          caption: caption.trim(),
          imageBase64: photo.base64,
          mediaType: "image/jpeg",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.error || "Something went wrong. Try again.");
      } else {
        setSuccess(text.done(data.barName || "your bar"));
        setPhoto(null);
        setCaption("");
      }
    } catch (e) {
      setError("Couldn't reach Bar Fun Finder. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={box}>
      <h2 style={{ margin: "0 0 6px", fontSize: "24px", color: "#fff" }}>{text.heading}</h2>
      <p style={{ margin: "0 0 16px", fontSize: "16px", color: "#B8B0C8" }}>
        {text.blurb}
      </p>

      {photo ? (
        <div style={{ position: "relative", marginBottom: "14px" }}>
          <img
            src={photo.preview}
            alt="Today's special preview"
            style={{ width: "100%", maxHeight: "360px", objectFit: "cover", borderRadius: "12px", display: "block" }}
          />
          <button
            type="button"
            onClick={() => setPhoto(null)}
            aria-label="Remove photo"
            style={{
              position: "absolute", top: "8px", right: "8px", width: "44px", height: "44px",
              borderRadius: "22px", border: "none", background: "rgba(0,0,0,0.65)", color: "#fff",
              fontSize: "22px", cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => fileInput.current && fileInput.current.click()}
          style={{
            ...bigButton,
            minHeight: "140px",
            marginBottom: "14px",
            background: "transparent",
            border: "2px dashed rgba(255,255,255,0.35)",
            color: "#fff",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          📷 Take a photo
          <div style={{ fontSize: "15px", fontWeight: 400, color: "#B8B0C8", marginTop: "6px" }}>
            or choose one from your phone
          </div>
        </button>
      )}
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={(e) => {
          pickPhoto(e.target.files && e.target.files[0]);
          e.target.value = "";
        }}
      />

      <label style={{ display: "block", fontSize: "16px", color: "#fff", marginBottom: "6px" }}>
        {text.label}
      </label>
      <input
        type="text"
        value={caption}
        maxLength={140}
        onChange={(e) => setCaption(e.target.value)}
        placeholder={text.placeholder}
        style={{
          width: "100%", boxSizing: "border-box", padding: "14px", fontSize: "17px",
          borderRadius: "10px", border: "1px solid rgba(255,255,255,0.15)",
          background: "#0D0B1A", color: "#fff", marginBottom: "14px",
        }}
      />

      <label style={{ display: "flex", gap: "10px", alignItems: "flex-start", margin: "0 0 14px", cursor: "pointer", color: "#D6D2E4", fontSize: "14px", lineHeight: 1.45 }}>
        <input
          type="checkbox"
          checked={termsOk}
          onChange={(e) => toggleTerms(e.target.checked)}
          style={{ width: "22px", height: "22px", flex: "0 0 auto", marginTop: "1px" }}
        />
        <span>
          I'm solely responsible for this photo and what I write. It's accurate, legal, and I have the right to post it.
          Bar Fun Finder doesn't review, sell or guarantee anything posted, and can remove any post. See the{" "}
          <a href="https://barfunfinder.com/partner-agreement.html" target="_blank" rel="noopener noreferrer" style={{ color: "#B79BFF" }}>Partner Agreement</a>.
        </span>
      </label>

      {error && <p style={{ color: "#FF6B8A", fontSize: "16px", margin: "0 0 12px" }}>{error}</p>}
      {success && <p style={{ color: "#4ADE80", fontSize: "16px", margin: "0 0 12px" }}>{success}</p>}

      <button
        type="button"
        onClick={post}
        disabled={busy || disabled}
        style={{
          ...bigButton,
          background: "linear-gradient(90deg, #FF3D81, #8B5CF6)",
          color: "#fff",
          opacity: busy || disabled ? 0.6 : 1,
        }}
      >
        {busy ? "Posting…" : "Post it"}
      </button>
    </div>
  );
}
